import {
  Box,
  Chip,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import EmailIcon from "@mui/icons-material/Email";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AddIcon from "@mui/icons-material/Add";
import { COLORS } from "../../assets/theme";
import ToastNotification from "../../components/ToastNotification";
import Actions from "../../components/common/Actions";
import CustomButton from "../../components/common/CustomButton";
import CustomCheckbox from "../../components/CustomCheckbox";
import CustomDialog from "../../components/common/CustomDialog";
import CustomLabel from "../../components/common/CustomLabel";
import CustomTable from "../../components/common/table/CustomTable";
import SearchHeaderContainer from "../../components/common/table/SearchHeaderContainer";
import {
  ConnectionTestResult,
  createInboundEmailConfig,
  deleteInboundEmailConfig,
  EmailProtocol,
  fetchActiveInboundEmailConfig,
  fetchInboundEmailConfigById,
  InboundEmailConfig,
  InboundEmailConfigParams,
  pollInboundEmailNow,
  PROTOCOL_OPTIONS,
  testInboundEmailConnection,
  updateInboundEmailConfig,
  useInboundEmailConfigsQuery,
} from "../../services/InboundEmailConfigService";
import { IField } from "../../types/table/interfaces.type";
import { hasJwtPermission } from "../../services/ServicesHelpers";

function prepareInboundEmailCreatePayload(
  data: Partial<InboundEmailConfig>,
): Partial<InboundEmailConfig> {
  const protocol = data.protocol ?? "IMAP";
  const out = { ...data };
  if (protocol === "MICROSOFT_OAUTH") {
    delete out.password;
    return out;
  }
  delete out.tenantId;
  delete out.clientId;
  delete out.clientSecret;
  return out;
}

function prepareInboundEmailUpdatePayload(
  data: Partial<InboundEmailConfig>,
): Partial<InboundEmailConfig> {
  const out = { ...data };
  const protocol = out.protocol ?? "IMAP";
  if (protocol === "MICROSOFT_OAUTH") {
    delete out.password;
    if (!String(out.clientSecret ?? "").trim()) delete out.clientSecret;
    if (!String(out.tenantId ?? "").trim()) delete out.tenantId;
    if (!String(out.clientId ?? "").trim()) delete out.clientId;
    return out;
  }
  delete out.tenantId;
  delete out.clientId;
  delete out.clientSecret;
  if (!String(out.password ?? "").trim()) delete out.password;
  return out;
}

function formatLastPollTimeForDisplay(lastPollTime?: string): string {
  if (!lastPollTime) return "-";
  const date = new Date(lastPollTime);
  if (Number.isNaN(date.getTime())) return lastPollTime;
  return date.toLocaleString(undefined, {
    timeZone: "UTC",
    timeZoneName: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
}

const InboundEmailConfigPage: React.FC = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [appliedFilters, setAppliedFilters] =
    useState<InboundEmailConfigParams>({
      search: "",
    });

  const dialogRef = useRef<InboundEmailDialogRef>(null);

  const handleOpenDialog = (mode: "add" | "edit" | "delete", id?: string) => {
    dialogRef.current?.openDialog(mode, id);
  };

  const handleSearchChange = (search: string) => {
    setAppliedFilters((prev) => ({ ...prev, search }));
  };

  const handleRefresh = () => {
    setAppliedFilters((prev) => ({ ...prev }));
    queryClient.invalidateQueries({ queryKey: ["inboundEmailConfig"] });
  };

  const actions = Actions({
    handleOpenEditModal: {
      handler: async (id: string | number) => {
        handleOpenDialog("edit", String(id));
      },
      isDisabled: !hasJwtPermission("GET_INBOUND_EMAIL_CONFIG"),
    },
    onDelete: {
      handler: async (id: string | number) => {
        handleOpenDialog("delete", String(id));
      },
      isDisabled: !hasJwtPermission("DELETE_INBOUND_EMAIL_CONFIG"),
    },
  });

  const fields: IField<InboundEmailConfig>[] = [
    {
      key: "enabled",
      header: "Status",
      transform: (t) => (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <CustomCheckbox
            checked={t.enabled}
            onChange={() => {}}
            ariaLabel="enabled status"
          />
        </Box>
      ),
    },
    { key: "emailAddress", header: "Email Address" },
    { key: "host", header: "Host" },
    { key: "port", header: "Port" },
    {
      key: "protocol",
      header: "Protocol",
      transform: (t) =>
        t.protocol === "MICROSOFT_OAUTH" ? "Microsoft (OAuth)" : t.protocol,
    },
    {
      key: "useSsl",
      header: "SSL",
      transform: (t) => (t.useSsl ? "Yes" : "No"),
    },
    { key: "folderName", header: "Folder" },
    {
      key: "pollingIntervalSeconds",
      header: "Poll Interval (s)",
    },
    {
      key: "lastPollTime",
      header: "Last Poll",
      transform: (t) => formatLastPollTimeForDisplay(t.lastPollTime),
    },
    {
      key: "lastPollError",
      header: "Last Error",
      transform: (t) => {
        if (!t.lastPollError) return "-";
        return (
          <Box
            sx={{
              maxWidth: 200,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: COLORS.errorRed,
            }}
            title={t.lastPollError}
          >
            {t.lastPollError}
          </Box>
        );
      },
    },
  ];

  return (
    <Box padding={isSmallScreen ? 2 : 3}>
      <Box sx={{ mb: 3 }}>
        <CustomLabel
          text="Inbound Email Configuration"
          variant={isSmallScreen ? "h6" : "h5"}
          fontWeight={500}
          mb={1}
        />
        <SearchHeaderContainer
          search={appliedFilters.search ?? ""}
          setSearch={handleSearchChange}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <AddIcon />,
              onClick: () => handleOpenDialog("add"),
              textColor: COLORS.iconColor,
              label: "Add Configuration",
              permission: "POST_INBOUND_EMAIL_CONFIG",
            },
          ]}
        />
        <CustomTable
          filters={appliedFilters}
          fields={fields}
          fetchFunc={useInboundEmailConfigsQuery}
          actions={actions}
          sortableFields={["emailAddress", "host", "protocol"]}
        />
      </Box>

      <InboundEmailDialog ref={dialogRef} onRefresh={handleRefresh} />
    </Box>
  );
};

interface InboundEmailDialogRef {
  openDialog: (mode: "add" | "edit" | "delete", id?: string) => void;
}

interface InboundEmailDialogProps {
  onRefresh: () => void;
}

const InboundEmailDialog = forwardRef<
  InboundEmailDialogRef,
  InboundEmailDialogProps
>(({ onRefresh }, ref) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"add" | "edit" | "delete">(
    "add",
  );
  const [dialogData, setDialogData] = useState<Partial<InboundEmailConfig>>({
    protocol: "IMAP",
    useSsl: true,
    enabled: false,
    pollingIntervalSeconds: 300,
    folderName: "INBOX",
  });
  const [selectedId, setSelectedId] = useState<string>("");
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [testResult, setTestResult] = useState<ConnectionTestResult | null>(
    null,
  );
  const [activeConfig, setActiveConfig] = useState<InboundEmailConfig | null>(
    null,
  );
  const [showEnableWarning, setShowEnableWarning] = useState(false);

  useImperativeHandle(ref, () => ({
    openDialog: (mode, id) => {
      setDialogMode(mode);
      setDialogOpen(true);
      setTestResult(null);
      setShowEnableWarning(false);
      openDialog(mode, id);
      setSelectedId(id ?? "");
    },
  }));

  useEffect(() => {
    const baseKeys: (keyof InboundEmailConfig)[] = [
      "emailAddress",
      "host",
      "port",
    ];
    const baseOk = baseKeys.every((field) => !!dialogData[field]);
    const protocol = dialogData.protocol ?? "IMAP";
    let credentialsOk = true;
    if (dialogMode === "add") {
      if (protocol === "MICROSOFT_OAUTH") {
        credentialsOk =
          !!String(dialogData.tenantId ?? "").trim() &&
          !!String(dialogData.clientId ?? "").trim() &&
          !!String(dialogData.clientSecret ?? "").trim();
      } else {
        credentialsOk = !!String(dialogData.password ?? "").trim();
      }
    }
    setIsSaveDisabled(!baseOk || !credentialsOk);
  }, [dialogData, dialogMode]);

  // Check for enable warning when enabled changes
  useEffect(() => {
    if (dialogData.enabled && activeConfig && activeConfig.id !== selectedId) {
      setShowEnableWarning(true);
    } else {
      setShowEnableWarning(false);
    }
  }, [dialogData.enabled, activeConfig, selectedId]);

  const openDialog = async (mode: "add" | "edit" | "delete", id?: string) => {
    setDialogMode(mode);

    // Fetch active config to check for warnings
    try {
      const active = await fetchActiveInboundEmailConfig();
      setActiveConfig(active);
    } catch {
      setActiveConfig(null);
    }

    if ((mode === "edit" || mode === "delete") && id) {
      try {
        const data = await fetchInboundEmailConfigById(id);
        setDialogData({
          ...data,
          password: "",
          clientSecret: "",
        });
      } catch (error: any) {
        ToastNotification.error(error.message);
        setDialogOpen(false);
      }
    } else {
      setDialogData({
        protocol: "IMAP",
        useSsl: true,
        enabled: false,
        pollingIntervalSeconds: 300,
        folderName: "INBOX",
      });
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setDialogData({});
    setTestResult(null);
    setShowEnableWarning(false);
  };

  const handleTestConnection = async () => {
    if (!selectedId) {
      ToastNotification.error(
        "Please save the configuration first before testing.",
      );
      return;
    }
    setIsTestingConnection(true);
    setTestResult(null);
    try {
      const result = await testInboundEmailConnection(selectedId);
      setTestResult(result);
      if (result.success) {
        ToastNotification.success(
          `Connection successful! ${
            result.unreadEmailCount !== undefined
              ? `Found ${result.unreadEmailCount} unread emails.`
              : ""
          }`,
        );
      } else {
        ToastNotification.error(result.message || "Connection test failed.");
      }
    } catch (error: any) {
      ToastNotification.error(error.message);
      setTestResult({ success: false, message: error.message });
    } finally {
      setIsTestingConnection(false);
    }
  };

  const handlePollNow = async () => {
    if (!selectedId) {
      ToastNotification.error("Please save the configuration first.");
      return;
    }
    setIsPolling(true);
    try {
      await pollInboundEmailNow(selectedId);
      ToastNotification.success("Email polling triggered successfully!");
      onRefresh();
    } catch (error: any) {
      ToastNotification.error(error.message);
    } finally {
      setIsPolling(false);
    }
  };

  const handleSave = async () => {
    try {
      if (dialogMode === "add") {
        await createInboundEmailConfig(
          prepareInboundEmailCreatePayload(dialogData),
        );
        ToastNotification.success("Configuration created successfully!");
      } else if (dialogMode === "edit" && selectedId) {
        await updateInboundEmailConfig(
          selectedId,
          prepareInboundEmailUpdatePayload(dialogData),
        );
        ToastNotification.success("Configuration updated successfully!");
      }
      onRefresh();
      closeDialog();
    } catch (error: any) {
      ToastNotification.error(error.message);
    }
  };

  const handleDelete = async () => {
    try {
      if (selectedId) {
        await deleteInboundEmailConfig(selectedId);
        ToastNotification.success("Configuration deleted successfully!");
        onRefresh();
        closeDialog();
      }
    } catch (error: any) {
      ToastNotification.error(error.message);
    }
  };

  const protocol = dialogData.protocol ?? "IMAP";
  const isMicrosoftOAuth = protocol === "MICROSOFT_OAUTH";
  const hostPlaceholder = isMicrosoftOAuth
    ? "e.g., outlook.office365.com"
    : "e.g., imap.gmail.com";

  return (
    <CustomDialog
      open={dialogOpen}
      onClose={closeDialog}
      mode={dialogMode}
      title={
        dialogMode === "add"
          ? "Add Inbound Email Configuration"
          : dialogMode === "edit"
            ? "Edit Inbound Email Configuration"
            : "Delete Configuration"
      }
      fieldsPerRow={dialogMode === "delete" ? 1 : 2}
      fields={
        dialogMode === "delete"
          ? []
          : [
              {
                type: "text",
                label: "Email Address",
                bgColor: COLORS.WhiteBackground,
                highlightLabelStar: true,
                value: dialogData.emailAddress ?? "",
                typeinput: "email",
                onChange: (value) => {
                  setDialogData((prev) => ({
                    ...prev,
                    emailAddress: String(value),
                  }));
                },
                placeholder: "e.g., rates@company.com",
              },
              {
                type: "select",
                label: "Protocol",
                value: dialogData.protocol ?? "IMAP",
                options: PROTOCOL_OPTIONS,
                onChange: (value) => {
                  setDialogData((prev) => ({
                    ...prev,
                    protocol: value as EmailProtocol,
                  }));
                },
              },
              {
                type: "text",
                label: "Host",
                bgColor: COLORS.WhiteBackground,
                highlightLabelStar: true,
                value: dialogData.host ?? "",
                onChange: (value) => {
                  setDialogData((prev) => ({
                    ...prev,
                    host: String(value),
                  }));
                },
                placeholder: hostPlaceholder,
              },
              {
                type: "text",
                label: "Port",
                bgColor: COLORS.WhiteBackground,
                highlightLabelStar: true,
                value: dialogData.port ?? "",
                typeinput: "number",
                onChange: (value) => {
                  setDialogData((prev) => ({
                    ...prev,
                    port: value === "" ? undefined : Number(value),
                  }));
                },
                placeholder: "e.g., 993",
              },
              ...(isMicrosoftOAuth
                ? [
                    {
                      type: "text" as const,
                      label: "Tenant ID",
                      bgColor: COLORS.WhiteBackground,
                      highlightLabelStar: dialogMode === "add",
                      value: dialogData.tenantId ?? "",
                      onChange: (value: unknown) => {
                        setDialogData((prev) => ({
                          ...prev,
                          tenantId: String(value),
                        }));
                      },
                      placeholder:
                        "Azure AD directory (tenant) ID, e.g. f9e8d7c6-xxxx...",
                    },
                    {
                      type: "text" as const,
                      label: "Client ID",
                      bgColor: COLORS.WhiteBackground,
                      highlightLabelStar: dialogMode === "add",
                      value: dialogData.clientId ?? "",
                      onChange: (value: unknown) => {
                        setDialogData((prev) => ({
                          ...prev,
                          clientId: String(value),
                        }));
                      },
                      placeholder:
                        "Azure application (client) ID, e.g. a1b2c3d4-xxxx...",
                    },
                    {
                      type: "text" as const,
                      label:
                        dialogMode === "edit"
                          ? "Client Secret (leave blank to keep current)"
                          : "Client Secret",
                      bgColor: COLORS.WhiteBackground,
                      highlightLabelStar: dialogMode === "add",
                      value: dialogData.clientSecret ?? "",
                      typeinput: "password" as const,
                      onChange: (value: unknown) => {
                        setDialogData((prev) => ({
                          ...prev,
                          clientSecret: String(value),
                        }));
                      },
                      placeholder:
                        dialogMode === "edit"
                          ? "••••••••"
                          : "Enter client secret",
                    },
                  ]
                : [
                    {
                      type: "text" as const,
                      label:
                        dialogMode === "edit"
                          ? "Password (leave blank to keep current)"
                          : "Password",
                      bgColor: COLORS.WhiteBackground,
                      highlightLabelStar: dialogMode === "add",
                      value: dialogData.password ?? "",
                      typeinput: "password" as const,
                      onChange: (value: unknown) => {
                        setDialogData((prev) => ({
                          ...prev,
                          password: String(value),
                        }));
                      },
                      placeholder:
                        dialogMode === "edit" ? "••••••••" : "Enter password",
                    },
                  ]),
              {
                type: "text",
                label: "Folder Name",
                bgColor: COLORS.WhiteBackground,
                value: dialogData.folderName ?? "INBOX",
                onChange: (value) => {
                  setDialogData((prev) => ({
                    ...prev,
                    folderName: String(value),
                  }));
                },
                placeholder: "e.g., INBOX",
              },
              {
                type: "text",
                label: "Polling Interval (seconds)",
                bgColor: COLORS.WhiteBackground,
                value:
                  dialogData.pollingIntervalSeconds !== undefined
                    ? dialogData.pollingIntervalSeconds
                    : "",
                typeinput: "number",
                onChange: (value) => {
                  setDialogData((prev) => ({
                    ...prev,
                    pollingIntervalSeconds:
                      value === "" ? undefined : Number(value),
                  }));
                },
                placeholder: "e.g., 300",
              },
              {
                type: "custom",
                component: <Box sx={{ width: "100%", height: "1px" }} />,
              },
              {
                type: "switch",
                label: "Use SSL",
                value: dialogData.useSsl ?? true,
                activeText: "Yes",
                inactiveText: "No",
                onChange: (value) => {
                  setDialogData((prev) => ({
                    ...prev,
                    useSsl: Boolean(value),
                  }));
                },
              },
              {
                type: "switch",
                label: "Enabled",
                value: dialogData.enabled ?? false,
                activeText: "Yes",
                inactiveText: "No",
                onChange: (value) => {
                  setDialogData((prev) => ({
                    ...prev,
                    enabled: Boolean(value),
                  }));
                },
              },
              // Status info for edit mode
              ...(dialogMode === "edit"
                ? [
                    {
                      type: "custom" as const,
                      component: (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            width: "100%",
                            p: 2,
                            backgroundColor: COLORS.lightGrayBackground,
                            borderRadius: 2,
                            gridColumn: "1 / -1",
                          }}
                        >
                          <CustomLabel
                            text="Status Information"
                            fontWeight={600}
                          />
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: 2,
                            }}
                          >
                            <Box>
                              <CustomLabel
                                text="Last Poll Time"
                                fontSize="0.85rem"
                              />
                              <Box sx={{ color: COLORS.typographyColor }}>
                                {formatLastPollTimeForDisplay(
                                  dialogData.lastPollTime,
                                )}
                              </Box>
                            </Box>
                            <Box>
                              <CustomLabel
                                text="Last Poll Error"
                                fontSize="0.85rem"
                              />
                              <Box
                                sx={{
                                  color: dialogData.lastPollError
                                    ? COLORS.errorRed
                                    : COLORS.typographyColor,
                                  wordBreak: "break-word",
                                }}
                              >
                                {dialogData.lastPollError || "-"}
                              </Box>
                            </Box>
                          </Box>

                          {/* Test Connection Result */}
                          {testResult && (
                            <Box
                              sx={{
                                p: 2,
                                borderRadius: 1,
                                backgroundColor: testResult.success
                                  ? "rgba(76, 175, 80, 0.1)"
                                  : "rgba(244, 67, 54, 0.1)",
                                border: `1px solid ${
                                  testResult.success
                                    ? COLORS.successGreen
                                    : COLORS.errorRed
                                }`,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  color: testResult.success
                                    ? COLORS.successGreen
                                    : COLORS.errorRed,
                                }}
                              >
                                {testResult.success ? (
                                  <CheckCircleIcon />
                                ) : (
                                  <CancelIcon />
                                )}
                                <span>
                                  {testResult.success
                                    ? `Connection successful!${
                                        testResult.unreadEmailCount !==
                                        undefined
                                          ? ` Found ${testResult.unreadEmailCount} unread emails.`
                                          : ""
                                      }`
                                    : testResult.message}
                                </span>
                              </Box>
                            </Box>
                          )}

                          {/* Warning for enabling when another config is active */}
                          {showEnableWarning && (
                            <Box
                              sx={{
                                p: 2,
                                borderRadius: 1,
                                backgroundColor: "rgba(255, 165, 0, 0.1)",
                                border: `1px solid ${COLORS.WarningOrange}`,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  color: COLORS.WarningOrange,
                                }}
                              >
                                <span>
                                  Warning: Another configuration (
                                  {activeConfig?.emailAddress}) is currently
                                  enabled. Enabling this configuration will
                                  disable the other one.
                                </span>
                              </Box>
                            </Box>
                          )}

                          {/* Action buttons */}
                          <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                            <CustomButton
                              label="Test Connection"
                              loading={isTestingConnection}
                              startIcon={<EmailIcon />}
                              onClick={handleTestConnection}
                              disabled={
                                isTestingConnection ||
                                isPolling ||
                                !hasJwtPermission(
                                  "TEST_INBOUND_EMAIL_CONFIG_CONNECTION",
                                )
                              }
                              bgColor={COLORS.neutralGray}
                              textColor="white"
                              borderRadius="8px"
                              size="small"
                            />
                            <CustomButton
                              label="Poll Now"
                              loading={isPolling}
                              startIcon={<PlayArrowIcon />}
                              onClick={handlePollNow}
                              disabled={
                                isTestingConnection ||
                                isPolling ||
                                !hasJwtPermission("POLL_INBOUND_EMAIL_CONFIG")
                              }
                              bgColor={COLORS.successGreen}
                              textColor="white"
                              borderRadius="8px"
                              size="small"
                            />
                          </Box>
                        </Box>
                      ),
                    },
                  ]
                : []),
            ]
      }
      buttons={
        dialogMode === "delete"
          ? [
              {
                label: "Cancel",
                variant: "outlined",
                bgColor: "transparent",
                textColor: COLORS.typographyColor,
                borderColor: COLORS.dividerColor,
                borderRadius: "12px",
                onClick: closeDialog,
              },
              {
                label: "Delete",
                variant: "contained",
                bgColor: COLORS.errorRed,
                borderRadius: "12px",
                onClick: handleDelete,
                permission: "DELETE_INBOUND_EMAIL_CONFIG",
              },
            ]
          : [
              {
                label: "Save",
                variant: "contained",
                bgColor: COLORS.GOLD,
                borderRadius: "12px",
                onClick: handleSave,
                disabled:
                  isSaveDisabled ||
                  (dialogMode === "edit"
                    ? !hasJwtPermission("PUT_INBOUND_EMAIL_CONFIG")
                    : !hasJwtPermission("POST_INBOUND_EMAIL_CONFIG")),
              },
            ]
      }
    />
  );
});

export default InboundEmailConfigPage;
